import type { ReportConfigurationSelection } from "./useReportConfigurationSelection";
import React, {
    useEffect,
    useState,
} from "react";
import {
    loadClients as loadClientsRequest,
    loadDataModels as loadDataModelsRequest,
    loadLookups as loadLookupsRequest,
    loadRuleOperators as loadRuleOperatorsRequest,
} from "../api/referenceData";
import {
    reportToast,
} from "../../../../components/notifications/appToast";
import {
    setActiveRuleOperators,
    makeEmptyConfigurationState,
    mapLookupGroupsToOptions,
    mapReportDataModels,
    mergeClientLookupsIntoData,
    normalizeLookupGroup,
    normalizeReportConfigurationClient,
} from "../reportConfigurationLogic";
import type {
    DataModel,
    MainTab,
    ModelKey,
    ReportConfig,
    ReportConfigurationClientLookup,
    ReportConfigurationLookupGroup,
    ReportConfigurationLookupOption,
    ReportConfigurationState,
} from "../types";

type ReferenceDataLoadingInputs = {
    initialSelection: ReportConfigurationSelection;
    backendDataModels: Record<string, DataModel>;
    setBackendDataModels: React.Dispatch<React.SetStateAction<Record<string, DataModel>>>;
    data: ReportConfigurationState;
    setData: React.Dispatch<React.SetStateAction<ReportConfigurationState>>;
    selectedPlanId: string;
    setSelectedPlanId: React.Dispatch<React.SetStateAction<string>>;
    setMainTab: React.Dispatch<React.SetStateAction<MainTab>>;
    setSelectedFolderId: React.Dispatch<React.SetStateAction<string>>;
    setSelectedReportId: React.Dispatch<React.SetStateAction<string>>;
    setExpandedFolders: React.Dispatch<React.SetStateAction<Set<string>>>;
    initialData: ReportConfigurationState | undefined;
};

export const useReferenceDataLoading = ({
    initialSelection,
    backendDataModels,
    setBackendDataModels,
    data,
    setData,
    selectedPlanId,
    setSelectedPlanId,
    setMainTab,
    setSelectedFolderId,
    setSelectedReportId,
    setExpandedFolders,
    initialData,
}: ReferenceDataLoadingInputs) => {
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientsLoaded, setClientsLoaded] = useState(false);
    const [clientsError, setClientsError] = useState("");
    const [dataModelsLoading, setDataModelsLoading] = useState(false);
    // Only used to trigger a re-render once rc.RuleOperator has loaded; the rows themselves
    // live in the logic module because buildSqlForSection and the validators read them too.
    const [, setRuleOperatorsLoaded] = useState(false);
    const [dataModelsError, setDataModelsError] = useState("");

    useEffect(() => {
        let isMounted = true;

        const loadClients = async () => {
            setClientsLoading(true);
            setClientsError("");

            try {
                const response = await loadClientsRequest();
                if (!isMounted) return;

                const clients = Array.isArray(response)
                    ? response.map(normalizeReportConfigurationClient).filter((client): client is ReportConfigurationClientLookup => !!client)
                    : [];

                if (!clients.length) {
                    const message = "No active health plans were returned from ClientInfo.";
                    setClientsError(message);
                    reportToast.warning("No health plans available", message);
                    return;
                }

                const preserveExistingConfig = !!initialData;
                const nextData = mergeClientLookupsIntoData(
                    preserveExistingConfig ? data : makeEmptyConfigurationState(),
                    clients,
                    preserveExistingConfig,
                );
                const preferredPlan = nextData.plans.find((plan) => plan.id === (initialSelection.selectedPlanId || selectedPlanId)) || nextData.plans[0];
                const restoreReportSelection = preferredPlan?.id === initialSelection.selectedPlanId;
                const preferredFolder = preferredPlan?.reportFolders.find((folder) => folder.id === initialSelection.selectedFolderId) || preferredPlan?.reportFolders[0];
                const preferredReport = preferredFolder?.reports.find((report) => report.id === initialSelection.selectedReportId) || preferredFolder?.reports[0];
                // Keep stored IDs until the report-plan request can validate them against its full tree.
                const nextFolderId = restoreReportSelection && initialSelection.selectedFolderId ? initialSelection.selectedFolderId : preferredFolder?.id || "";
                const nextReportId = restoreReportSelection && initialSelection.selectedReportId ? initialSelection.selectedReportId : preferredReport?.id || "";

                setData(nextData);
                setSelectedPlanId(preferredPlan?.id || "");
                setSelectedFolderId(nextFolderId);
                setSelectedReportId(nextReportId);
                setExpandedFolders(new Set(nextFolderId ? [nextFolderId] : []));
                setMainTab(initialSelection.mainTab || "reports");
                setClientsLoaded(true);
            } catch (error) {
                if (isMounted) {
                    console.log(error);
                    const message = "Unable to load health plans from ClientInfo.";
                    setClientsError(message);
                    reportToast.error("Health plans did not load", reportToast.messageFromError(error, message));
                }
            } finally {
                if (isMounted) {
                    setClientsLoading(false);
                }
            }
        };

        loadClients();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadDataModels = async () => {
            setDataModelsLoading(true);
            setDataModelsError("");

            try {
                const response = await loadDataModelsRequest();
                if (!isMounted) return;

                const mappedModels = mapReportDataModels(Array.isArray(response) ? response : []);
                if (!Object.keys(mappedModels).length) {
                    const message = "No active DataModel records were returned.";
                    setDataModelsError(message);
                    setBackendDataModels({});
                    reportToast.warning("Data models unavailable", message);
                    return;
                }

                setBackendDataModels(mappedModels);
            } catch (error) {
                if (isMounted) {
                    console.log(error);
                    const message = "Unable to load data models from the backend.";
                    setDataModelsError(message);
                    setBackendDataModels({});
                    reportToast.error("Data models did not load", reportToast.messageFromError(error, message));
                }
            } finally {
                if (isMounted) {
                    setDataModelsLoading(false);
                }
            }
        };

        // rc.RuleOperator drives which operators each data type offers, and their labels.
        // A failure here is not fatal: operatorCodesForDataType falls back to the built-in
        // table, which is a transcription of the same rows, so the builder stays usable.
        const loadRuleOperators = async () => {
            try {
                const response = await loadRuleOperatorsRequest();
                if (!isMounted) return;
                setActiveRuleOperators(Array.isArray(response) ? response : []);
                setRuleOperatorsLoaded(true);
            } catch (error) {
                if (!isMounted) return;
                console.log(error);
                setRuleOperatorsLoaded(true);
            }
        };

        loadDataModels();
        loadRuleOperators();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!Object.keys(backendDataModels).length) return;

        const validModelKeys = new Set(Object.keys(backendDataModels));
        setData((current) => {
            let changed = false;
            const plans = current.plans.map((plan) => ({
                ...plan,
                reportFolders: plan.reportFolders.map((folder) => ({
                    ...folder,
                    reports: folder.reports.map((report) => {
                        const nextModels = report.models.filter((modelKey) => validModelKeys.has(modelKey));
                        const nextDataModelIds = nextModels
                            .map((modelKey) => backendDataModels[modelKey]?.dataModelId)
                            .filter((id): id is number => !!id);
                        const nextModelBasedOn = Object.entries(report.modelBasedOn).reduce((acc, [modelKey, value]) => {
                            if (validModelKeys.has(modelKey)) acc[modelKey as ModelKey] = value;
                            return acc;
                        }, {} as ReportConfig["modelBasedOn"]);

                        const nextSections = report.sections.filter((section) => validModelKeys.has(section.model));
                        const reportChanged = nextModels.length !== report.models.length
                            || nextDataModelIds.length !== (report.dataModelIds || []).length
                            || Object.keys(nextModelBasedOn).length !== Object.keys(report.modelBasedOn).length
                            || nextSections.length !== report.sections.length;
                        changed = changed || reportChanged;

                        return reportChanged ? {
                            ...report,
                            models: nextModels,
                            dataModelIds: nextDataModelIds,
                            modelBasedOn: nextModelBasedOn,
                            sections: nextSections,
                        } : report;
                    }),
                })),
            }));

            return changed ? { ...current, plans } : current;
        });
    }, [backendDataModels, data.plans]);
    return {
        clientsLoading,
        clientsLoaded,
        clientsError,
        dataModelsLoading,
        dataModelsError,
    };
};
type LookupLoadingInputs = {
    setLookupGroups: React.Dispatch<React.SetStateAction<ReportConfigurationLookupGroup[]>>;
    setLookupOptionsByKey: React.Dispatch<React.SetStateAction<Record<string, ReportConfigurationLookupOption[]>>>;
};

export const useLookupLoading = ({
    setLookupGroups,
    setLookupOptionsByKey,
}: LookupLoadingInputs) => {
    const [lookupsLoading, setLookupsLoading] = useState(false);
    const [lookupsError, setLookupsError] = useState("");
    const [lookupsLoaded, setLookupsLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadLookups = async () => {
            setLookupsLoading(true);
            setLookupsError("");
            setLookupsLoaded(false);
            setLookupGroups([]);
            setLookupOptionsByKey({});

            try {
                const response = await loadLookupsRequest();
                if (!isMounted) return;

                const groups = Array.isArray(response?.groups ?? response?.Groups)
                    ? (response.groups ?? response.Groups).map(normalizeLookupGroup).filter((group: ReportConfigurationLookupGroup | null): group is ReportConfigurationLookupGroup => !!group)
                    : [];
                const mappedLookups = mapLookupGroupsToOptions(groups);

                if (!Object.keys(mappedLookups).length) {
                    const message = "No report lookup values were returned.";
                    setLookupsError(message);
                    reportToast.warning("Report lookup values unavailable", message);
                    return;
                }

                setLookupGroups(groups);
                setLookupOptionsByKey(mappedLookups);
                setLookupsLoaded(true);
            } catch (error) {
                if (isMounted) {
                    console.log(error);
                    const message = "Unable to load report lookup values. Backend lookup values are required before saving.";
                    setLookupsError(message);
                    setLookupGroups([]);
                    setLookupOptionsByKey({});
                    setLookupsLoaded(false);
                    reportToast.error("Report lookup values did not load", reportToast.messageFromError(error, message));
                }
            } finally {
                if (isMounted) {
                    setLookupsLoading(false);
                }
            }
        };

        loadLookups();

        return () => {
            isMounted = false;
        };
    }, []);
    return {
        lookupsLoading,
        lookupsError,
        lookupsLoaded,
    };
};
